'use client';
import { useId, useState, type FormEvent } from 'react';
import parse from 'html-react-parser';
import { Input } from 'components/UI/Input';
import styles from './name-oracle.module.scss';

function getNameOracleResultText(sum: number): string {
  const resultTexts = [
    'Die Namenszahl 1 zeichnet zielorientierte und energische Personen aus. Sie sind klassische Karrieretypen, die soziale Kontakte pflegen und sich durch ihre gewinnende Art viele Freunde machen. Unabhängigkeit und Individualität sind Einsern wichtig und prägen ihren Lebensstil. Mit viel Initiative in die richtige Richtung, schaffen sie es bis ganz nach oben.<br />Auf der anderen Seite kann sich der Ehrgeiz der Einser-Menschen auch negativ ausprägen. Eine gewisse Aggressivität und ihre Sturheit wirken dann eher abschreckend. Kritik können sie nicht gut vertragen. Das hat zur Folge, dass sich Einser oft einsam fühlen. Sie können ihre wahren Gefühle schlecht zeigen und schießen durch ihre eigene Sensibilität oft übers Ziel hinaus und verletzen andere mit ihren kritischen Äußerungen.',
    'Personen mit der Namenszahl 2 sind ausgeglichene Menschen. Nicht zuletzt deshalb, weil sie harmoniebedürftig sind und oft auf Kompromisse eingehen. Sie kooperieren lieber und verhalten sich diplomatisch, als sich Konflikten auszusetzen. Das macht sie anpassungsfähig und passiv. Sie sind rücksichtsvolle Zuhörer und hilfsbereit. Auf ihre Loyalität kann man sich verlassen. Ihre eigenen Bedürfnisse geraten dadurch allerdings oft in den Hintergrund.<br />Durch ihre Zurückhaltung und ihre Selbstzweifel stehen sich Zweier-Menschen manchmal selbst im Weg. Sie wirken oft unsicher, sogar unterwürfig und können meist nicht über ihren eigenen Schatten springen. So entgehen ihnen viele Chancen im Leben.',
    'Die Namenszahl 3 steht für Menschen mit einer großen Lebenslust. Sie strotzen meist vor Energie und Kreativität. Sie können sich gut durchsetzen, sind kommunikativ und wortgewandt. Sie nehmen ihr Leben mit Leichtigkeit. Eine gewisse Oberflächlichkeit macht sie allerdings leicht manipulierbar. Auch ihr Bestreben, anderen zu gefallen, steuert sie manchmal in die falsche Richtung.<br />Dass sich hinter der Frohnatur eines Dreier-Menschen auch tiefgründige Gedanken verbergen können, wird oft verkannt. Dadurch fehlt den sonst so starken Dreiern in ihrem Leben meist eine verständnisvolle Schulter zum Anlehnen. Die manchmal extravagante Art der Dreier kann auf andere mitunter großspurig und eitel wirken.',
    'Personen mit der Namenszahl 4 zeichnen sich durch ihre Zuverlässigkeit und Geduld aus. Sie lieben Ordnung in ihrem Leben, erledigen alles mit Präzision und nach Plan. Mit viel Fleißarbeit erreichen sie ihre Ziele. Sie sind hilfsbereit, reagieren selbst auf Kritik und die Hilfe anderer aber abweisend. Abweichungen von gewohnten Abläufen werfen sie aus der Bahn.<br />Vierer-Menschen lassen sich nur ungern in ihrem Tun stören und sind im Zwischenmenschlichen eher verschlossen. Doch man kann sich auf sie verlassen und ihnen vertrauen, denn sie sind aufrichtig und zeigen offen, woran man bei ihnen ist. Allerdings fühlen sich andere auch oft von Vierern kontrolliert.',
    'Fünfer-Menschen lassen die sprichwörtliche Fünf in ihrem Leben allzu gerne gerade sein. Sie lieben das Abenteuer, sind offen für alles, fantasieren gerne. Mit ihren Talenten brauchen sie ständig neue Herausforderungen. Trott ist nichts für Personen mit der Namenszahl 5. Sie sind kontaktfreudig und haben das Herz auf dem rechten Fleck. Sie ecken mit niemandem an und sind überall zu Hause.<br />Ihre Rastlosigkeit bringt aber auch Unruhe und Unstetigkeit in ihr Leben. Auf ihrer ständigen Suche nach Neuem wird es für Fünfer schwierig, sich zu binden oder sich in ihrem Leben auf einen Ort oder einen Beruf festzulegen. Das stört jedoch nur die ihnen nahestehenden Mitmenschen.',
    'Menschen mit der Namenszahl 6 lieben es harmonisch. Darum zählen Treue, Aufrichtigkeit und Zuverlässigkeit zu ihren dominierenden Eigenschaften. Soziale Kontakte haben eine große Bedeutung und werden durch Gastfreundschaft und Hilfsbereitschaft erhalten. Sechser-Menschen sind sympathische Zuhörer und haben viele Freunde.<br />Durch ihren Beschützerinstinkt erdrücken Sechser ihre Mitmenschen manchmal. Gleichzeitig vernachlässigen sie sich in ihrem Mitgefühl und in ihrer Sorge um andere oft selbst. Ihr großes Verantwortungsbewusstsein könnten sie häufiger für sich selbst einsetzen. In ihrer perfektionistischen Art wirken Sechser auf andere oft belehrend und stur.',
    'Personen mit der Namenszahl 7 wirken durch ihren Hang zur Melancholie oft geistesabwesend und introvertiert. Das macht sie zu Einzelgängern, denn sie scheinen unnahbar, manchmal sogar arrogant. Dabei sind sie in Wahrheit wissbegierig, analytisch und originell. Sie überdenken alles ganz genau. Dabei lassen Siebener ihren Gefühlen selten freien Lauf – ihr Verstand dominiert.<br />Mehr Spontaneität würde Siebenern manchmal mehr bringen als sich den Kopf zu zerbrechen. Ihre Reserviertheit erschwert es Personen mit der Namenszahl 7 zudem, Freunde zu finden. Gleichzeitig macht sie diese Eigenschaft in einer einmal geschlossenen Freundschaft zu einer treuen und loyalen Seele. In ihrem Leben bringt also nicht Eigeninitiative sie weiter, sondern Beharrlichkeit, Fleiß und Zuverlässigkeit.',
    'Für Menschen mit der Namenszahl 8 stehen Macht und Erfolg im Mittelpunkt. Dafür geben sie alles. Sie sind pragmatisch und materialistisch veranlagt. Das gleiche Maß an Engagement erwarten Achter auch von ihren Mitmenschen. Dabei sind ihnen die Gefühle anderer oft egal, ihr Verhalten ist meist taktlos. Gleichzeitig haben Achter auf andere eine fast schon magische Anziehungskraft. Wenn Sie wollen, können Achter warmherzig sein und ihre Energie auch für andere einsetzen.<br />Ihre eigenen Gefühle zeigen Personen mit der Namenszahl 8 nur selten. Allerdings handeln sie immer ihren Bedürfnissen entsprechend und haben gerne erfolgreiche Leute um sich. Irgendetwas fehlt in ihrem Leben, deshalb sind sie immer auf der Suche. Achter würden weniger aggressiv wirken, wenn sie ihre Kraft positiv nutzen und mehr Menschlichkeit an den Tag legen würden.',
    'Personen mit der Namenszahl 9 zeichnen sich durch Menschlichkeit und Natürlichkeit aus. Sie sind feinfühlig und verbindlich. Sie setzen sich für andere, meist Schwächere, ein und halten mit ihrer Meinung nicht hinter dem Berg. Das polarisiert jedoch auch.<br />Ihre Selbstlosigkeit steht Neunern oft im Weg. Ihre Gabe, immer den richtigen Ton zu treffen, könnten sie häufiger für sich selbst einsetzen. Besonders, wenn es darum geht, ihr großes kreatives Potenzial zu nutzen und Ideen in die Tat umzusetzen. Ihr Idealismus schießt jedoch dann manchmal übers Ziel hinaus und wirkt auf andere oft selbstgefällig.',
  ];

  return resultTexts[sum - 1];
}

function calculateNameOracle(firstName: string, lastName: string): number {
  const charValue: Record<string, number> = {
    a: 1,
    b: 2,
    c: 3,
    d: 4,
    e: 5,
    f: 6,
    g: 7,
    h: 8,
    i: 9,
    j: 1,
    k: 2,
    l: 3,
    m: 4,
    n: 5,
    o: 6,
    p: 7,
    q: 8,
    r: 9,
    s: 1,
    t: 2,
    u: 3,
    v: 4,
    w: 5,
    x: 6,
    y: 7,
    z: 8,
    ö: 2,
    ä: 6,
    ü: 8,
    ß: 2,
  };

  const name = (firstName.toLowerCase() + lastName.toLowerCase()).trim();
  let sum = 0;

  for (let i = 0; i < name.length; i++) {
    const char = name.charAt(i);
    if (charValue[char] !== undefined) {
      sum += charValue[char];
    }
  }

  while (sum > 9) {
    const digits = sum.toString().split('').map(Number);
    sum = digits.reduce((acc, digit) => acc + digit, 0);
  }

  return sum;
}

export const WidgetToolsCalculatorNameOracle = () => {
  const firstNameId = useId();
  const lastNameId = useId();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [result, setResult] = useState<number | null>(null);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (firstName.trim() && lastName.trim()) {
      const nameSum = calculateNameOracle(firstName, lastName);

      setResult(nameSum);
    }
  }

  return (
    <div className={styles.root}>
      <form onSubmit={onSubmit}>
        <div className={styles.fieldWrapper}>
          <Input
            id={firstNameId}
            label="Vorname(n)"
            value={firstName}
            onChange={(event) => {
              setFirstName(event.currentTarget.value);
            }}
            required
          />
          <Input
            id={lastNameId}
            label="Nachname"
            value={lastName}
            onChange={(event) => {
              setLastName(event.currentTarget.value);
            }}
            required
          />
        </div>

        <button className={styles.button} type="submit">
          Auswerten
        </button>
      </form>

      {result !== null && (
        <div className={styles.result} aria-live="polite">
          <div className={styles.headline}>Deine Namenszahl: {result}</div>
          <p>{parse(getNameOracleResultText(result))}</p>
        </div>
      )}
    </div>
  );
};
